<script setup lang="ts">
import { useHead } from '@unhead/vue'
import AsmeB401 from '@/components/PressureAccuracyAsmeB401.vue'
import AsmeB407 from '@/components/PressureAccuracyAsmeB407.vue'
import ResourceDisclaimer from '@/components/ResourceDisclaimer.vue'

// HEADER
useHead({
  title: 'Pressure Accuracy',
  meta: [
    {
      name: 'description',
      content: 'How accurate do you need to be?'
    }
  ]
})
</script>

<template>
  <div class="mb-6">
    <h1 class="text-4xl text-center text-primary-800 m-0">
      Pressure Accuracy
    </h1>
  </div>
  <section class="p-2 lg:p-6 surface-section border-round">
    <article>
      <div class="text-5xl text-primary-800">
        How accurate do you need to be?
      </div>
      <div class="text-xl text-600 mb-4">
        That depends on your application needs or industry requirements.
      </div>

      <p>
        Gauge accuracy is one of the factors in determining the best pressure gauge for your application.
      </p>
      <p>
        <strong class="text-red-500">Accuracy always has a cost.</strong> The more accurate the instrument, typically the more expensive. Lack of accuracy, can cost even more if the quality of the end process result suffers.
      </p>
      <p>
        The most important thing to consider when determining your accuracy needs are the the engineering requirements of your process and any applicable regulation requirements. But, for a starting point, these are some industry recommendations
        <a
          href="#"
          class="text-no-decoration"
        >
          <fa-icon
            icon="fa fa-asterisk"
            class="text-primary"
          />
        </a>
        :
      </p>
      <dl class="ml-6">
        <dt class="text-xl text-red-500 font-bold">
          <fa-icon
            icon="fa fa-chevron-circle-right"
            class="text-primary mr-2"
          />
          Test Gauges and Standards
        </dt>
        <dd class="ml-5">
          0.25% through 0.10% full-scale accuracies
        </dd>
        <dt class="mt-2 text-xl text-red-500 font-bold">
          <fa-icon
            icon="fa fa-chevron-circle-right"
            class="text-primary mr-2"
          />
          Critical Processes
        </dt>
        <dd class="ml-5">
          0.5% full-scale accuracy
        </dd>
        <dt class="mt-2 text-xl text-red-500 font-bold">
          <fa-icon
            icon="fa fa-chevron-circle-right"
            class="text-primary mr-2"
          />
          General Industrial Processes
        </dt>
        <dd class="ml-5">
          1.0% accuracy
        </dd>
        <dt class="mt-2 text-xl text-red-500 font-bold">
          <fa-icon
            icon="fa fa-chevron-circle-right"
            class="text-primary mr-2"
          />
          Less Critical Commercial Processes
        </dt>
        <dd class="ml-5">
          2.0% accuracy
        </dd>
      </dl>
    </article>
    <article class="mt-6">
      <div class="text-5xl text-primary-800">
        What is ±3-2-3% of span accuracy?
      </div>
      <div class="grid">
        <div class="col-12 md:col-8 lg:col-7">
          <p>
            Accuracies on gauges are typically listed as a grade like <strong class="text-primary">B</strong> or <strong class="text-primary">2A</strong>, or written as <strong class="text-primary">±3-2-3% of span accuracy</strong>.
          </p>
          <p>
            These ratings come from <a href="#charts">two charts</a> that are often considered the standard in grading accuracy, the ASME B40.1 for dial-type gauges and the ASME B40.7 for digital gauges.
          </p>
          <div class="text-3xl text-primary-800">
            What's the difference in the grades and spans?
          </div>
          <p>
            The grades are shorthand assignments to the accuracy span in that grade. The accuracy span percentage is based on the the entire range of the gauge. So 1% of a 100# gauge is 1psi and 3% of a 300# gauge is 9psi. The smaller the percentage in the accuracy rating the more accurate the reading is.
          </p>

          <h3 class="text-700 text-xl my-4">
            <em class="font-weight-regular">Example</em>: 100# Pressure Gauge
          </h3>

          <div class="text-xl font-bold text-red-500">
            ±1% of span accuracy
          </div>
          <p class="mt-0">
            Never more than <b>1 psi</b> over or under the pressure reading in ideal conditions<span class="red--text">*</span>.
          </p>
          <div class="text-xl font-bold text-red-500">
            ±0.1% of span accuracy
          </div>
          <p class="mt-0">
            Never more than <b>0.1 psi</b> over or under the pressure reading in ideal conditions<span class="red--text">*</span>.
          </p>
          <div class="text-xl font-bold text-red-500">
            ±3-2-3% of span accuracy
          </div>
          <p class="mt-0">
            Acccuracies with multiple percentages indicate that the accuracy varies at different reading points on the gauge. These type of gauges are most accurate in the middle half of their range. So then margins of error on our 100# pressure gauge would be:
          </p>
          <ul>
            <li>
              <strong>3psi or ±3%</strong> for 0-25 psi range of the gauge
            </li>
            <li>
              <strong>2psi or ±2%</strong> for 26-75 psi range of the gauge
            </li>
            <li>
              <strong>3psi or ±3%</strong> for 76-100 psi range of the gauge
            </li>
          </ul>
        </div>
        <div class="col-12 md:col-offset-1 md:col-3 lg:col-4">
          <div class="border-1 border-300 border-round border-red-500 p-3">
            <div class="flex">
              <fa-icon
                icon="fa fa-info-circle"
                class="text-4xl mr-4 text-red-500"
              />
              <div>
                It is important to remember that the accuracies are based on <strong>ideal environmental conditions</strong>.
              </div>
            </div>
            <dl class="text-center">
              <dt class="text-primary text-xl">
                Temperature
              </dt>
              <dd class="text-lg">
                68&deg;F ±2&deg;F / 20&deg;C ± 1&deg;C
              </dd>
              <dt class="text-primary text-xl mt-3">
                Barometric Pressure
              </dt>
              <dd class="text-lg">
                29.92 in. Hg / 101.32 kPa
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </article>
  </section>
  <div
    id="charts"
    class="my-6"
  >
    <h2 class="text-3xl text-center text-primary-800 m-0">
      ASME Accuracy Charts
    </h2>
  </div>
  <section class="p-2 lg:p-6 surface-section border-round">
    <article class="grid">
      <div class="col-12">
        <p class="mt-4">
          The ASME Charts below are available for a quick reference of grades and accuracies.
          <!-- You can also filter by accuracies like <router-link to="/product-search/pressure-gauges?accuracies=±%201%25%20of%20span">±1% of span accuracy</router-link> in our <router-link to="/product-search/pressure-gauges?accuracies=±%201%25%20of%20span">Product Search</router-link> -->
        </p>
      </div>
      <div class="col-12 lg:col-6">
        <asme-b401 />
      </div>
      <div class="col-12 lg:col-6">
        <asme-b407 />
      </div>
    </article>
    <div class="flex border-top-1 border-bottom-1 border-300 py-3 my-3">
      <fa-icon
        icon="fa fa-info-circle"
        class="mr-3 text-primary"
      />
      <div class="font-italic">
        The information above is derived from B40.1 &amp; B40.7 contained in ASME B40.100. Printed copies are available from ASME, Three Park Ave, New York NY 10016. You can visit their website at
        <a
          class="no-underline"
          href="https://asme.org/"
          target="_blank"
          rel="noopener"
        >
          asme.org
        </a>.
      </div>
    </div>
    <resource-disclaimer />
  </section>
</template>
